import { clipToRange, type Observation, STRIP_RANGE_MS } from "../lib/observations";

/**
 * Minimum segment width in percent. w-16 is 64px; this floor ensures a
 * single-sample interval (one poll) stays visible at ~1px: 1.5625 / 100 * 64 ≥ 1.
 * Rounded to 1.5625% for exact 1px at 64px width.
 */
const MIN_SEGMENT_PCT = 1.5625;

/**
 * Time-proportional in-stock strip. Replaces Sparkbar, which assumed a point
 * series: equal-width bars would misrepresent intervals of uneven length.
 *
 * In-stock spans are the only saturated thing here, and a single-sample
 * interval is floored to a visible width but stays visibly narrower than a
 * sustained span — a one-poll blip must not look like a day of availability.
 *
 * The `live` prop identifies which segment, if any, represents the current
 * product state. observed_to lags wall-clock by up to a sweep interval
 * (~10 minutes), so proximity to `now` cannot identify the current state;
 * the caller (product row) knows the current state and must say so.
 */
export function InStockStrip({
	observations,
	now,
	live = false,
	rangeMs = STRIP_RANGE_MS,
}: {
	observations: Observation[];
	now: number;
	live?: boolean;
	rangeMs?: number;
}) {
	const clipped = clipToRange(observations, now, rangeMs);
	if (clipped.length === 0) return null;
	const start = now - rangeMs;
	const inStockSegments = clipped.filter((o) => o.inStock);
	const liveIndex = live ? inStockSegments.length - 1 : -1;

	return (
		<span
			className="relative inline-block h-1.5 w-16 overflow-hidden rounded-full bg-hairline align-middle"
			title={`In-stock spans, last ${Math.round(rangeMs / 86_400_000)} days`}
			aria-hidden
		>
			{inStockSegments.map((o, i) => {
				const rawLeft = ((o.from - start) / rangeMs) * 100;
				const width = Math.max(MIN_SEGMENT_PCT, ((o.to - o.from) / rangeMs) * 100);
				const left = Math.min(rawLeft, 100 - width);
				return (
					<span
						key={i}
						style={{ left: `${left}%`, width: `${width}%` }}
						className={`absolute inset-y-0 ${i === liveIndex ? "bg-foil" : "bg-muted"}`}
					/>
				);
			})}
		</span>
	);
}
